import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs';
import { ActionSystem } from 'src/app/domain/ActionSystem';
import { IUser } from 'src/app/domain/registration/models/IUser';
import { GlobalUtils } from 'src/app/shared/global-utils';
import { LoadingService } from 'src/app/shared/services/loading/loading.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { AuthService } from 'src/app/views/auth/shared/auth.service';
import { UserService } from '../crud.service';
import { MatDialog } from '@angular/material/dialog';
import { IFieldConfig } from 'src/app/domain/IFieldConfig';
import { CustomFormValidators } from 'src/app/shared/form-validators/custom';
import { UserGroupService } from '../../user-groups/crud-service';
import { CES_CONFIG_FIELDS } from '../crud-config';
import { UserPermissionsComponent } from '../../user-permissions/user-permissions.component';

@Component({
  selector: 'app-user-ces',
  templateUrl: './crud-ces.component.html',
  styleUrls: ['./crud-ces.component.sass'],
})
export class UserCESComponent implements OnInit {
  config = CES_CONFIG_FIELDS;
  public form: FormGroup;
  public user: IUser | null;
  public action: ActionSystem;
  public filteredCities: any[] = [];
  public userId: string | null = null;
  public selectedPermissions: any[] = [];
  public permissionCount: number = 0;

  private groupsLoaded = false;
  private branchesLoaded = false;
  private branchOptions: any[] = [];
  private selectedBranchId: string | null = null;
  private _pendingEditData: any = null;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private service: UserService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private toastService: ToastService,
    private loadingService: LoadingService,
    private groupService: UserGroupService,
    private dialog: MatDialog
  ) {
    this.userId = this.route.snapshot.params['id'];
    this.action = new ActionSystem(this.route);
    this.user = this.authService.getUser();
    this.groupService
      .listAll({ getRelation: 1, limit: 100, page: 0, filter: '' })
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          const groups = Array.isArray(response) && Array.isArray(response[0])
            ? response[0]
            : response;
          this.getFieldConfig('userGroups').options = groups || [];
          this.groupsLoaded = true;
          this.patchRelationsOnEdit();
        },
      });

    this.service
      .listBranchOptions()
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          const branches = Array.isArray(response?.options)
            ? response.options
            : Array.isArray(response)
              ? response
              : [];
          this.branchOptions = branches;
          this.selectedBranchId = response?.selectedBranchId ?? null;
          this.getFieldConfig('branches').options = this.branchOptions;
          this.applyBranchValidation(this.branchOptions);
          this.branchesLoaded = true;
          this.patchRelationsOnEdit();
        },
      });
  }

  /**
   * Retorna true se o usuario logado esta editando a si mesmo E e admin/root.
   * Nesses casos, campos criticos (email, username, userGroups) ficam bloqueados.
   */
  get isSelfAdminEdit(): boolean {
    if (this.action.isCreateAction()) return false;
    if (!this.userId || !this.user?.id) return false;
    if (String(this.userId) !== String(this.user.id)) return false;
    const group = String((this.user as any)?.nameGroupPermission || '');
    return ['ENTERPRISE_ADM', 'DEVELOPER'].includes(group);
  }

  private lockAdminSelfProtectedFields(): void {
    if (!this.isSelfAdminEdit) return;
    this.form.get('email')?.disable();
    this.form.get('username')?.disable();
    this.form.get('userGroups')?.disable();
  }

  ngOnInit(): void {
    this.initForm();
    this.lockAdminSelfProtectedFields();

    if (!this.action.isCreateAction() && this.userId) {
      this.form.get('password')?.clearValidators();
      this.form.get('confirmPassword')?.clearValidators();
      this.loadingService.show();
      this.service
        .get(this.userId)
        .pipe(take(1))
        .subscribe({
          next: (response: any) => {
            this._pendingEditData = response;
            this.patchRelationsOnEdit();
            const { userGroups, branches, branchIds, ...rest } = response;
            this.form.patchValue({
              ...rest,
              image: response.avatarUrl,
            });
          },
          error: (e: any) => {
            console.log(e);
          },
          complete: () => this.loadingService.hide(),
        });
    }
  }

  private patchRelationsOnEdit() {
    if (this._pendingEditData && this.groupsLoaded && this.branchesLoaded) {
      const groups = Array.isArray(this._pendingEditData.userGroups)
        ? this._pendingEditData.userGroups
        : [];
      this.form.get('userGroups')?.setValue(groups);
      this.form.get('branches')?.setValue(this.resolveBranchesForEdit(this._pendingEditData));
      this._pendingEditData = null;
    }
  }

  private resolveBranchesForEdit(userData: any): any[] {
    const branches = Array.isArray(userData?.branches) ? userData.branches : [];
    const selectedIds = Array.isArray(userData?.branchIds)
      ? userData.branchIds
      : branches.map((branch: any) => branch?.id).filter(Boolean);

    return selectedIds
      .map((id: string) => this.branchOptions.find((option) => String(option?.id) === String(id)) || branches.find((branch: any) => String(branch?.id) === String(id)))
      .filter(Boolean);
  }

  public initForm() {
    this.form = this.fb.group({
        image: [null, []],
        isActive: [true, [Validators.required]],
        userGroups: [[], [Validators.required]],
        branches: [[], []],
        firstName: [null, [Validators.required]],
        lastName: [null, [Validators.required]],
        document: [null, [Validators.required]],
        email: [null, [Validators.required, Validators.email]],
        username: [null, [Validators.required]],
        phone: [null, [Validators.required]],
        password: [null, [Validators.required]],
        confirmPassword: [null, [Validators.required]],
        requireChangePassword: [true, []],
        permissions: [null, []],
      },
      {validators: [CustomFormValidators()]}
      );
    this.applyBranchValidation(this.branchOptions);
  }

  public async onSubmit() {
    const userRegister: IUser = {
      ...this.form.value,
    };

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastService.showError('Preencha os campos corretamente!');
      return;
    }

    const formData = new FormData();
    (userRegister as any).permissions = this.selectedPermissions;
    (userRegister as any).branchIds = this.normalizeSelectedBranchIds((userRegister as any).branches);

    const userName = `${userRegister.firstName.trim()}_${userRegister.lastName.trim()}`;

    const imageSrc = this.form.get('image')?.value;
    if (imageSrc && imageSrc.includes(';base64')) {
      formData.append(
        'image',
        new File(
          [await GlobalUtils.blobImageFromBase64String(imageSrc)],
          imageSrc?.toLowerCase().indexOf('png') != -1
            ? `${userName}_image.png`
            : `${userName}_image.jpeg`
        )
      );
    }
    userRegister.image = undefined;

    formData.append('data', JSON.stringify(userRegister));
    if (this.action.isEditAction() && this.userId) {
      this.service
        .updateWithFormData(this.userId, formData)
        .pipe(take(1))
        .subscribe({
          next: (result) => this.router.navigate(['users']),
          error: (e) => console.log('err', e),
        });
    } else {
      this.service
        .createWithFormData(formData)
        .pipe(take(1))
        .subscribe({
          next: (result) => this.router.navigate(['users']),
          error: (e) => console.log('err', e),
        });
    }
  }

  public getFieldConfig(name: string): any {
    return this.config.find((c) => c.name === name);
  }

  private applyBranchValidation(branches: any[]) {
    const control = this.form?.get('branches');
    if (!control) {
      return;
    }

    if ((branches || []).length > 1) {
      control.setValidators([Validators.required]);
      // Na criacao, ja vem pre-selecionada a filial em que o usuario da sessao esta logado.
      if (
        this.action?.isCreateAction() &&
        (!control.value || control.value.length === 0) &&
        this.selectedBranchId
      ) {
        const current = branches.find(
          (branch) => String(branch?.id) === String(this.selectedBranchId)
        );
        if (current) {
          control.setValue([current]);
        }
      }
    } else {
      control.clearValidators();
      if ((branches || []).length === 1 && (!control.value || control.value.length === 0)) {
        control.setValue([branches[0]]);
      }
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  private normalizeSelectedBranchIds(branches: any[]): string[] {
    if (!Array.isArray(branches)) {
      return [];
    }

    return [...new Set(
      branches
        .map((branch: any) => typeof branch === 'string' ? branch : branch?.id)
        .filter((id: string | undefined) => !!id)
    )];
  }

  public openPermissionsDialog() {
    const dialogRef = this.dialog.open(UserPermissionsComponent, {
      width: '700px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: { element: this.user },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      const permissions = result?.element?.permissions || result?.permissions || [];
      this.selectedPermissions = permissions;
      this.permissionCount = this.selectedPermissions.length;
      this.form.get('permissions')?.setValue(this.selectedPermissions);
    });
  }
}
